
import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { BuildingType } from '../../types';
import { BUILDINGS } from '../../constants';
import { getHash, applyGrayscale } from '../../utils/mapUtils';

// Geometries - Created once globally to share geometry buffers across all instances
const boxGeo = new THREE.BoxGeometry(1, 1, 1);
const plateGeo = new THREE.BoxGeometry(1, 0.2, 1);
const cylinderGeo = new THREE.CylinderGeometry(1, 1, 1, 16);
const coneGeo = new THREE.ConeGeometry(0.5, 0.8, 4);
const domeGeo = new THREE.SphereGeometry(0.5, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
const ringGeo = new THREE.TorusGeometry(0.3, 0.05, 8, 16);
const wedgeGeo = new THREE.CylinderGeometry(0, 0.5, 1, 4);
const warningGeo = new THREE.OctahedronGeometry(0.3, 0); // Geometric replacement for warning text

interface BuildingMeshProps {
  type: BuildingType;
  baseColor: string;
  x: number;
  y: number;
  opacity?: number;
  transparent?: boolean;
  isConnected?: boolean;
  level?: number;
}

const ProceduralBuilding = React.memo(({ type, baseColor, x, y, opacity = 1, transparent = false, isConnected = true, level = 1 }: BuildingMeshProps) => {
  const groupRef = useRef<THREE.Group>(null);
  const warningRef = useRef<THREE.Mesh>(null);
  const scaleRef = useRef(1);
  
  const hash = getHash(x, y);
  const rotation = Math.floor(hash * 4) * (Math.PI / 2);
  
  // Target scale calculation for level upgrades
  const targetScale = 1 + ((level - 1) * 0.25);
  
  // Animation Logic: Smooth scale up when level changes
  useFrame((state, delta) => {
      if (groupRef.current) {
          scaleRef.current = THREE.MathUtils.lerp(scaleRef.current, targetScale, delta * 8);
          groupRef.current.scale.set(1, scaleRef.current, 1);
      }
      if (warningRef.current) {
          warningRef.current.rotation.y += delta * 2;
          warningRef.current.position.y = 2 + ((hash % 0.3) - 0.15) + Math.sin(state.clock.elapsedTime * 4) * 0.1;
      }
  });

  const heightVar = ((hash % 0.3) - 0.15); // Static height variance
  const detailVar = (hash * 100) % 1; // 0-1 for random details
  
  // Performance: Only enable transparency calculation if actually needed
  // Disconnected buildings are ghosted (0.5 opacity), requiring transparency
  const effectiveOpacity = isConnected ? opacity : 0.5;
  const isTransparent = transparent || !isConnected || opacity < 1;
  
  // Memoize Colors to prevent recalculation on every render
  const { mainColor, darkColor, grayColor, neonColor } = useMemo(() => {
    const c = new THREE.Color(baseColor);
    
    // If disconnected, desaturate the colors
    const main = isConnected ? c : applyGrayscale(c.clone(), 0.8);
    const dark = isConnected ? c.clone().multiplyScalar(0.5) : applyGrayscale(c.clone().multiplyScalar(0.5), 0.8);
    
    return { 
        mainColor: main, 
        darkColor: dark, 
        grayColor: applyGrayscale(c.clone().multiplyScalar(0.5), 0.8), // Always gray for base plate
        neonColor: c 
    };
  }, [baseColor, isConnected]);
  
  // Shared Material Properties
  const matProps = useMemo(() => ({
      opacity: effectiveOpacity,
      transparent: isTransparent,
      roughness: 0.3,
      metalness: 0.1,
      flatShading: false
  }), [effectiveOpacity, isTransparent]);

  // Materials - Re-created only when visual state changes
  const materials = useMemo(() => ({
      main: new THREE.MeshStandardMaterial({ color: mainColor, ...matProps }),
      dark: new THREE.MeshStandardMaterial({ color: darkColor, ...matProps }),
      // Accent is always white-ish, but needs to respect opacity
      accent: new THREE.MeshStandardMaterial({ color: '#f8fafc', ...matProps }),
      glass: new THREE.MeshStandardMaterial({ 
          color: '#bae6fd', 
          transparent: true, // Glass is always transparent
          opacity: 0.6 * effectiveOpacity, 
          roughness: 0.1, 
          metalness: 0.9,
      }),
      neon: new THREE.MeshStandardMaterial({ 
          color: neonColor, 
          emissive: neonColor, 
          emissiveIntensity: isConnected ? 0.8 : 0, // Lights off when disconnected
          ...matProps 
      }),
      warning: new THREE.MeshBasicMaterial({ color: '#ef4444', wireframe: true })
  }), [mainColor, darkColor, neonColor, matProps, effectiveOpacity, isConnected]);

  const commonProps = { castShadow: true, receiveShadow: true };
  const config = BUILDINGS[type];
  const category = config?.category;

  return (
    <group rotation={[0, rotation, 0]} position={[0, 0, 0]}>
        {/* Animated Scaled Group */}
        <group ref={groupRef}>
            {/* Base Plate - Always Dark/Gray */}
            <mesh {...commonProps} material={materials.dark} geometry={plateGeo} position={[0, 0.1, 0]} />

            {(() => {
                // --- Unique Models ---
                
                if (type === BuildingType.Firewall) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.dark} geometry={boxGeo} position={[0, 0.5 + heightVar, 0]} scale={[0.9, 0.8, 0.4]} />
                        <mesh {...commonProps} material={materials.neon} geometry={boxGeo} position={[0, 0.5 + heightVar, 0]} scale={[0.95, 0.6, 0.2]} />
                        {[0.2, -0.2].map(px => (
                            <mesh key={px} {...commonProps} material={materials.main} geometry={boxGeo} position={[px, 1.0 + heightVar, 0]} scale={[0.1, 0.4, 0.1]} />
                        ))}
                        </>
                    );
                }

                if (type === BuildingType.ComplianceMgmt) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.main} geometry={boxGeo} position={[0, 0.4 + heightVar, 0]} scale={[0.9, 0.6, 0.9]} />
                        <mesh {...commonProps} material={materials.dark} geometry={boxGeo} position={[0, 0.8 + heightVar, 0]} scale={[0.7, 0.4, 0.7]} />
                        <mesh {...commonProps} material={materials.glass} geometry={boxGeo} position={[0, 0.9 + heightVar, 0]} scale={[0.5, 0.6, 0.5]} />
                        </>
                    );
                }

                if (type === BuildingType.OpsFunding) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.main} geometry={boxGeo} position={[0, 0.5, 0]} scale={[0.95, 0.8, 0.8]} />
                        {[-0.4, 0.4].map(px => (
                            <mesh key={px} {...commonProps} material={materials.dark} geometry={cylinderGeo} position={[px, 0.5, 0.4]} scale={[0.1, 0.8, 0.1]} />
                        ))}
                        <mesh {...commonProps} material={materials.dark} geometry={wedgeGeo} position={[0, 1.1, 0]} rotation={[0, Math.PI/4, 0]} scale={[1, 0.4, 1]} />
                        </>
                    );
                }

                if (type === BuildingType.LoadBalancer) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.main} geometry={boxGeo} position={[0, 0.5 + heightVar, 0]} scale={[0.8, 0.6, 0.8]} />
                        <mesh {...commonProps} material={materials.neon} geometry={cylinderGeo} position={[0, 0.8 + heightVar, 0]} scale={[0.4, 0.1, 0.4]} />
                        <mesh {...commonProps} material={materials.accent} geometry={boxGeo} position={[0, 0.9 + heightVar, 0]} scale={[0.6, 0.1, 0.6]} />
                        </>
                    );
                }
                
                if (type === BuildingType.WAF) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.dark} geometry={boxGeo} position={[0, 0.5 + heightVar, 0]} scale={[0.9, 0.6, 0.9]} />
                        <mesh {...commonProps} material={materials.neon} geometry={boxGeo} position={[0, 0.6 + heightVar, 0.45]} scale={[0.7, 0.4, 0.1]} />
                        <mesh {...commonProps} material={materials.glass} geometry={boxGeo} position={[0, 1.0 + heightVar, 0]} scale={[0.6, 0.6, 0.6]} />
                        </>
                    );
                }
                
                if (type === BuildingType.Vault) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.dark} geometry={boxGeo} position={[0, 0.5, 0]} scale={[0.9, 0.8, 0.9]} />
                        <mesh {...commonProps} material={materials.neon} geometry={cylinderGeo} position={[0, 0.5, 0.45]} rotation={[Math.PI/2, 0, 0]} scale={[0.3, 0.1, 0.3]} />
                        <mesh {...commonProps} material={materials.main} geometry={boxGeo} position={[0, 1.0, 0]} scale={[0.8, 0.2, 0.8]} />
                        {(detailVar > 0.5 || level >= 3) && <mesh {...commonProps} material={materials.accent} geometry={coneGeo} position={[0.3, 1.2, 0.3]} scale={[0.2, 0.3, 0.2]} />}
                        </>
                    );
                }
                
                if (type === BuildingType.JumpGate) {
                    return (
                        <>
                        <mesh {...commonProps} material={materials.dark} geometry={cylinderGeo} position={[0, 0.1, 0]} scale={[0.8, 0.1, 0.8]} />
                        <mesh {...commonProps} material={materials.neon} geometry={cylinderGeo} position={[0, 0.8, 0]} rotation={[Math.PI/2, 0, 0]} scale={[0.7, 0.1, 0.7]} />
                        {level >= 3 && <mesh {...commonProps} material={materials.glass} geometry={ringGeo} position={[0, 0.8, 0]} rotation={[0,0,0]} scale={[1.2, 1.2, 1.2]} />}
                        </>
                    );
                }

                if (type === BuildingType.Core) {
                    // Core uses a specific unique Gold material for the crown that glows
                    const coreCrownMat = isConnected 
                        ? new THREE.MeshStandardMaterial({ color: '#fbbf24', emissive: '#fbbf24', emissiveIntensity: 0.2, ...matProps })
                        : materials.dark;

                    return (
                    <>
                        <mesh {...commonProps} material={materials.main} geometry={boxGeo} position={[0, 0.5, 0]} scale={[0.85, 0.8, 0.85]} />
                        {[[-0.35,-0.35], [0.35,-0.35], [-0.35,0.35], [0.35,0.35]].map(([x,z], i) => (
                        <mesh key={i} {...commonProps} material={materials.dark} geometry={cylinderGeo} position={[x, 0.6, z]} scale={[0.2, 1.2, 0.2]} />
                        ))}
                        <mesh {...commonProps} material={coreCrownMat} geometry={coneGeo} position={[0, 1.4, 0]} scale={[0.8, 0.6, 0.8]} />
                    </>
                    );
                }

                if (category === 'Cyber') {
                    return (
                    <>
                        <mesh {...commonProps} material={materials.dark} geometry={boxGeo} position={[0, 0.6 + heightVar, 0]} scale={[0.8, 1.0, 0.8]} />
                        <mesh {...commonProps} material={materials.neon} geometry={boxGeo} position={[0, 0.6 + heightVar, 0.41]} scale={[0.6, 0.9, 0.05]} />
                        <mesh {...commonProps} material={materials.neon} geometry={boxGeo} position={[0, 0.6 + heightVar, -0.41]} scale={[0.6, 0.9, 0.05]} />
                        {type === BuildingType.Internet ? (
                            <mesh {...commonProps} material={materials.glass} geometry={domeGeo} position={[0, 1.2 + heightVar, 0]} scale={[0.5, 0.3, 0.5]} />
                        ) : (
                            <group position={[0, 1.2 + heightVar, 0]}>
                                <mesh {...commonProps} material={materials.accent} geometry={boxGeo} scale={[0.9, 0.1, 0.9]} />
                                {(detailVar > 0.7 || level >= 2) && <mesh {...commonProps} material={materials.accent} geometry={cylinderGeo} position={[0.3, 0.2, 0]} scale={[0.1, 0.4, 0.1]} />}
                            </group>
                        )}
                        {level >= 4 && (
                            <mesh {...commonProps} material={materials.neon} geometry={ringGeo} position={[0, 0.5, 0]} rotation={[Math.PI/2, 0, 0]} scale={[0.6, 0.6, 0.6]} />
                        )}
                    </>
                    );
                }

                // Generic & Fallback for Office/Standard Types
                return (
                    <group>
                        <mesh {...commonProps} material={materials.main} geometry={boxGeo} position={[0, 0.5 + heightVar, 0]} scale={[0.8, 1 + heightVar, 0.8]} />
                        {detailVar > 0.5 && <mesh {...commonProps} material={materials.dark} geometry={boxGeo} position={[0.2, 1.1 + heightVar, 0.2]} scale={[0.3, 0.3, 0.3]} />}
                        {detailVar < 0.3 && <mesh {...commonProps} material={materials.accent} geometry={cylinderGeo} position={[-0.2, 1.1 + heightVar, -0.2]} scale={[0.2, 0.2, 0.2]} />}
                        {level >= 3 && <mesh {...commonProps} material={materials.glass} geometry={boxGeo} position={[0, 1.2 + heightVar, 0]} scale={[0.6, 0.2, 0.6]} />}
                        {level >= 5 && <mesh {...commonProps} material={materials.neon} geometry={cylinderGeo} position={[0, 1.4 + heightVar, 0]} scale={[0.1, 0.5, 0.1]} />}
                    </group>
                );
            })()}
        </group>
      
      {!isConnected && type !== BuildingType.None && type !== BuildingType.Pipeline && (
          <mesh ref={warningRef} position={[0, 2 + heightVar, 0]} material={materials.warning} geometry={warningGeo} />
      )}
    </group>
  );
});

export default ProceduralBuilding;
